import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { SHELL_BANNER_SOURCES, type ShellBannerSource } from '@/stores/shell-banner-sources'

/** The shell banner: the first visible, undismissed source in precedence order, dismissible for the session until its condition clears. */
export const useShellBannerStore = defineStore('shell-banner', () => {
  const sources = SHELL_BANNER_SOURCES

  const dismissed = ref<ReadonlySet<string>>(new Set())
  const suppressed = ref(false)

  const visibleIds = computed(() => sources.filter((source) => source.visible()).map((s) => s.id))

  watch(visibleIds, (ids) => {
    const kept = [...dismissed.value].filter((id) => ids.includes(id))
    if (kept.length !== dismissed.value.size) dismissed.value = new Set(kept)
  })

  const banner = computed<ShellBannerSource | null>(() => {
    if (suppressed.value) return null
    return (
      sources.find(
        (source) => visibleIds.value.includes(source.id) && !dismissed.value.has(source.id),
      ) ?? null
    )
  })

  /** Hides the banner on routes whose page already shows the state; a suppressed banner keeps its dismissals. */
  function setSuppressed(value: boolean): void {
    suppressed.value = value
  }

  /** Dismisses the banner now showing, for as long as its condition holds. */
  function dismiss(): void {
    const current = banner.value
    if (current) dismissed.value = new Set([...dismissed.value, current.id])
  }

  return { banner, setSuppressed, dismiss }
})
